<template>
  <RouterLink
    :to="logoRoute"
    :aria-label="ariaLabel"
    :class="logoLinkClasses"
  >
    <!-- Logo Image -->
    <div class="flex-shrink-0">
      <img
        :src="currentLogo"
        :alt="logoAlt"
        :class="imageClasses"
      />
    </div>
    
    <!-- Logo Text -->
    <div v-if="showText" class="min-w-0">
      <div :class="textClasses" class="flex min-w-0 items-center gap-2 flex-wrap">
        {{ text }}
        <slot name="trailing" />
      </div>
      <div v-if="subtitle && showSubtitle" :class="subtitleClasses">
        {{ subtitle }}
      </div>
    </div>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '@/stores/auth'

// Import logo images
import lightLogo from '@/assets/images/rukh-logo-black.png'
import darkLogo from '@/assets/images/rukh-logo-white.png'

interface Props {
  href?: string
  ariaLabel?: string
  logoAlt?: string
  text?: string
  subtitle?: string
  showText?: boolean
  showSubtitle?: boolean
  size?: 'small' | 'medium' | 'large' | 'extra-large'
  variant?: 'default' | 'minimal'
}

const props = withDefaults(defineProps<Props>(), {
  href: '/',
  ariaLabel: 'RUKH ERP Home',
  logoAlt: 'RUKH Logo',
  text: 'RUKH ERP',
  subtitle: 'Enterprise Reasoning Platform',
  showText: true,
  showSubtitle: true,
  size: 'medium',
  variant: 'default'
})

const authStore = useAuthStore()

// Compute route based on authentication status
const logoRoute = computed(() => {
  // If href is explicitly provided and different from default, use it
  if (props.href && props.href !== '/') {
    return props.href
  }
  
  // If user is authenticated, route to home welcome page
  if (authStore.isAuthenticated) {
    return '/home'
  }
  
  // Otherwise route to home/login page
  return '/'
})

// CSS-only theme management
const isDark = ref(document.documentElement.classList.contains('dark'))

// Computed logo source
const currentLogo = computed(() => {
  return isDark.value ? darkLogo : lightLogo
})

// Watch for theme changes from other components
let observer: MutationObserver | null = null

onMounted(() => {
  // Watch for class changes on documentElement
  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        isDark.value = document.documentElement.classList.contains('dark')
      }
    })
  })
  
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  })
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

// Computed classes
const logoLinkClasses = computed(() => {
  const baseClasses = 'flex items-center space-x-3 '
  
  const sizeClasses = {
    small: 'space-x-2',
    medium: 'space-x-3',
    large: 'space-x-4',
    'extra-large': 'space-x-6'
  }
  
  const variantClasses = {
    default: 'hover:opacity-80',
    minimal: 'hover:opacity-90'
  }
  
  return [
    baseClasses,
    sizeClasses[props.size as keyof typeof sizeClasses],
    variantClasses[props.variant as keyof typeof variantClasses]
  ].join(' ')
})

// Image size classes
const imageClasses = computed(() => {
  const imageSizeClasses = {
    small: 'h-8 w-auto',
    medium: 'h-12 w-auto',
    large: 'h-16 w-auto',
    'extra-large': 'h-20 w-auto'
  }
  
  return imageSizeClasses[props.size as keyof typeof imageSizeClasses]
})

// Text size classes
const textClasses = computed(() => {
  const textSizeClasses = {
    small: 'text-lg font-bold text-text',
    medium: 'text-2xl font-bold text-text',
    large: 'text-3xl font-bold text-text',
    'extra-large': 'text-4xl font-bold text-text'
  }
  
  return textSizeClasses[props.size as keyof typeof textSizeClasses]
})

// Subtitle size classes
const subtitleClasses = computed(() => {
  const subtitleSizeClasses = {
    small: 'text-xs text-text-muted',
    medium: 'text-xs text-text-muted',
    large: 'text-base text-text-muted',
    'extra-large': 'text-lg text-text-muted'
  }
  
  return subtitleSizeClasses[props.size as keyof typeof subtitleSizeClasses]
})
</script>
